import AuthStore from '@stores/AuthStore'
import { isNil, isString } from 'lodash'
import { mapFind } from '@helpers/ArrayHelper'
import { titleize } from '@helpers/StringHelper'

const CURRENT_LOCALE = 'en'
const PLACEHOLDER = /(?:\{\{|%\{)(.*?)(?:\}\}?)/gm // Accepts `{{placeholder}}` and `%{placeholder}`.

function missingPlaceholder (name, message, options) {
  throw new TypeError(`Placeholder '${name}' was not provided for the translation: "${message}".`, { name, message, options })
}

function interpolate (message, options = {}) {
  const matches = message.match(PLACEHOLDER)

  if (!matches) {
    return message
  }

  let value

  while (matches.length) {
    const placeholder = matches.shift()
    const name = placeholder.replace(PLACEHOLDER, '$1')

    if (!isNil(options[name])) {
      value = options[name].toString().replace(/\$/gm, '_#$#_')
    } else {
      missingPlaceholder(name, message, options)
    }

    const regex = new RegExp(placeholder.replace(/\{/gm, '\\{').replace(/\}/gm, '\\}'))
    message = message.replace(regex, value)
  }

  return message.replace(/_#\$#_/g, '$')
}

export function translateIfKey (name) {
  return (name.includes('.') && translate(name)) || name
}

// Lodash get treats a leftover numeric step as a character index, so a path
// like staff_survey_access.0 reads "S" out of "Survey Availability Dates".
// Stop when the path walks past a string.
function translationAt (root, path) {
  return path.split('.').reduce((node, part) => {
    if (node == null || typeof node !== 'object') return undefined
    return node[part]
  }, root)
}

export function translate (name, options = {}) {
  const key = [options.prefix, name].filter(x => x).join('.')
  const locales = [
    AuthStore.careProvider.translation && `${CURRENT_LOCALE}-${AuthStore.careProvider.translation}`,
    CURRENT_LOCALE,
  ]
  const translation = mapFind(locales, locale => locale && translationAt(window.translations, `${locale}.${key}`))
  return isString(translation) ? interpolate(translation, options) : undefined
}

export function translateWithFallback (key, fallbacks) {
  return mapFind(fallbacks, options => options && translate(key, options))
}

function labelFallbacks (prefix) {
  return [
    (prefix && { prefix: `simple_form.labels.${prefix}` }),
    { prefix: 'simple_form.labels.defaults' },
  ]
}

function labelForParts (parts, prefix) {
  return parts
    .filter(part => !/^\d+$/.test(part))
    .map(part => translateWithFallback(part, labelFallbacks(prefix)) || titleize(part))
    .join(' ')
}

export function translateLabel (name, { prefix } = {}) {
  const exact = translateWithFallback(name, labelFallbacks(prefix))
  if (exact) return exact

  const parts = String(name).split('.').filter(Boolean)
  for (let length = parts.length - 1; length > 0; length--) {
    const parent = translateWithFallback(parts.slice(0, length).join('.'), labelFallbacks(prefix))
    if (!parent) continue

    const tail = parts.slice(length)
    const indexes = tail.filter(part => /^\d+$/.test(part))
    const indexLabel = indexes.length === 1 ? String(Number(indexes[0]) + 1) : ''
    const suffix = [indexLabel, labelForParts(tail, prefix)].filter(Boolean).join(' ')
    return suffix ? `${parent} ${suffix}` : parent
  }

  return labelForParts(parts, prefix) || titleize(name)
}

export function translateHint (name, { prefix } = {}) {
  if (prefix) {
    return translate(name, { prefix: `simple_form.hints.${prefix}` })
  }
}

export function translatePlaceholder (name, { prefix } = {}) {
  if (prefix) {
    return translate(name, { prefix: `simple_form.placeholders.${prefix}` })
  }
}

// Public: Helper to get label, hint, and placeholder for a FormInput. Notice
// that if any were passed explicitly, such as :label="false", they are kept.
export function getTranslationsFrom (field, { hint, label, placeholder }, translationOptions = {}) {
  const formHint = hint !== undefined ? hint : translateHint(field, translationOptions)

  return {
    hint: formHint,
    label: label !== undefined ? label : translateLabel(field, translationOptions),
    placeholder: placeholder !== undefined ? placeholder : translatePlaceholder(field, translationOptions),
  }
}
